// src/pages/PendingApproval.js
import React, { useState, useEffect, useContext } from 'react';
import Sidebar from '../../components/sidebar/Sidebar';
import axios from '../../api/axios';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Modal,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './PendingApproval.css';
import { AuthContext } from '../../context/AuthContext';

const carouselSlides = [
  {
    image:
      'https://smu.edu.ph/wp-content/uploads/2023/09/366365685_3612711768995875_4613685649229899442_n.jpg',
    title: 'Campus Life',
    description: 'Experience the vibrant SMU community',
  },
  {
    image:
      'https://smu.edu.ph/wp-content/uploads/2023/09/382548523_217296047805896_1706455415830682916_n.jpg',
    title: 'Alumni Connections',
    description: 'Building networks that last a lifetime',
  },
  {
    image: 'https://smu.edu.ph/wp-content/uploads/2024/08/medtech.png',
    title: 'Medical Technology',
    description: 'Advancing healthcare through innovative education',
  },
];

const PendingApproval = () => {
  const { user } = useContext(AuthContext);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === carouselSlides.length - 1 ? 0 : prev + 1,
      );
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get('/api/events');
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchEvents();
  }, []);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setModalOpen(true);
  };

  const eventsOnSelectedDate = events.filter(
    (event) =>
      selectedDate &&
      new Date(event.date).toDateString() === selectedDate.toDateString(),
  );

  const tileContent = ({ date }) => {
    const eventCount = events.filter(
      (event) => new Date(event.date).toDateString() === date.toDateString(),
    ).length;
    return (
      <div className="dots-container">
        {[...Array(eventCount)].map((_, i) => (
          <span key={i} className="event-dot" />
        ))}
      </div>
    );
  };

  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography variant="h6">Loading your dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'row' }}>
      <Sidebar />
      <Box
        sx={{
          marginLeft: isMobile ? 0 : '250px',
          width: '100%',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Paper elevation={0} square sx={{ p: 3, mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Welcome to SMU Alumni Portal, {user.firstName} {user.lastName}
          </Typography>
        </Paper>

        {/* Layout Adjusted for Mobile vs Desktop */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            flexGrow: 1,
            overflowY: 'auto',
          }}
        >
          {/* Profile + Calendar come first on mobile */}
          <Box
            sx={{
              flex: isMobile ? '1 1 100%' : 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              square
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Avatar
                src={user.photo || '/default-avatar.png'}
                sx={{ width: 100, height: 100, mb: 2 }}
              />
              <Typography variant="h6">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2">{user.email}</Typography>
              <Typography variant="body2">ID: {user.idNo}</Typography>
            </Paper>

            <Paper elevation={0} square sx={{ p: 2 }}>
              <Calendar
                onClickDay={handleDateClick}
                tileContent={tileContent}
              />
            </Paper>
          </Box>

          {/* Carousel Section */}
          <Paper
            elevation={0}
            square
            sx={{
              flex: isMobile ? '1 1 100%' : 4,
              p: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div className="carousel-container">
              {carouselSlides.map((slide, index) => (
                <div
                  key={index}
                  className={`carousel-slide ${
                    index === currentSlide ? 'active' : ''
                  }`}
                  style={{ backgroundImage: `url(${slide.image})` }}
                >
                  <div className="carousel-content">
                    <h2>{slide.title}</h2>
                    <p>{slide.description}</p>
                  </div>
                </div>
              ))}
              <div className="carousel-indicators">
                {carouselSlides.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${
                      index === currentSlide ? 'active' : ''
                    }`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </Paper>
        </Box>
      </Box>

      {/* Events Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: 400,
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" mb={2}>
            Events on {selectedDate?.toDateString()}
          </Typography>
          {eventsOnSelectedDate.length ? (
            <List>
              {eventsOnSelectedDate.map((event, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={event.title}
                    secondary={`${new Date(
                      event.date,
                    ).toLocaleTimeString()} - ${event.location}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No events</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default PendingApproval;
